"use strict";

const HardAssetInventoryModalController = function () {
  const inventoryFormKey = "hard-asset-inventory";

  const catchBasinDataViews = ["muni-catch-basin", "dry-weather"];

  const newAssetDefaultsByAssetType = {
    bmpFcs: () => ({
      bmpType: catchBasinDataViews.includes(Tree.get("dataView")) ? 12 : 16,
      installDate: DateTime.getTodayIso(),
      phase: "completed",
      // Stored as bmp_ptype, which inventory completeness requires for every BMP type,
      // so a BMP without it can never be benchmarked
      landOwnership: "our-agency",
      responsiblePartyMaintenance: "our-agency",
      systemAccess: true,
      treatedStormType: "24-hr-percentile",
      treatedStormPercentile: "85th",
    }),
    // Cleanouts and lift stations store a rotation too, but only outfalls draw it on the
    // marker, so only outfalls start with one
    outfall: () => ({
      rotationAzimuth: 0,
      installDate: DateTime.getTodayIso(),
      phase: "completed",
    }),
  };

  var getNewAssetDefaults = function () {
    const defaultsForAssetType = newAssetDefaultsByAssetType[currentAssetType];

    return defaultsForAssetType ? defaultsForAssetType() : {};
  };

  let currentAssetType = null;
  let currentAssetId = null;
  let appliedNewAssetDefaults = {};

  var canEditInventory = function (assetType) {
    return UserRoles.getRolePermissionForUser(
      HardAssetInventoryConfig.getEditPermissionKey(assetType),
    );
  };

  var invokeHardAssetInventoryModal = async function (id, assetType, startingPage) {
    currentAssetType = assetType;
    currentAssetId = id ?? null;

    const data = await ApiCalls.getHardAssetForInventoryModal(assetType, currentAssetId);
    appliedNewAssetDefaults = {};
    Tree.set(["asset", inventoryFormKey], isNewAsset() ? withNewAssetDefaults(data) : data);
    resetAllPages();

    InventoryModal.setOpenInventory(inventoryFormKey);
    await InventoryModal.showModal(
      getInventoryPages(),
      startingPage,
      {},
      {
        saveAndCloseHandler: saveAndCloseModal,
        cancelHandler: closeInventory,
        formHasUpdatesHandler: () => !noUpdatesInForm(),
      },
    );

    setModalTitle(data);
    InventoryModal.setHideInventoryOnClose(true);
    InventoryModal.restoreNavigationButtons();
    loadFormListener();
    updateMonitoringScreeningStep();
    updateSaveButtonState();
  };

  // The API only sends the fields the asset type has, so defaults never add a field
  var withNewAssetDefaults = function (data) {
    const defaults = getNewAssetDefaults();
    const defaultedData = { ...data };
    for (const field in defaults) {
      if (defaultedData[field] === null) {
        defaultedData[field] = defaults[field];
        appliedNewAssetDefaults[field] = defaults[field];
      }
    }
    return defaultedData;
  };

  var isNewAsset = function () {
    return currentAssetId === null;
  };

  var getInventoryPages = function () {
    return HardAssetInventoryConfig.getInventoryPages(currentAssetType).map(
      (pageKey) => getModulesByPage()[pageKey],
    );
  };

  var getModulesByPage = function () {
    return {
      setLocation: HardAssetInventorySetLocation,
      generalInfo: HardAssetInventoryGeneralInfo,
      drainageArea: HardAssetInventoryDrainageArea,
    };
  };

  // Map state a page leaves behind belongs to the asset that was open, not to the next one
  var resetAllPages = function () {
    const allPages = [...Object.values(getModulesByPage()), HardAssetInventoryMonitoringScreening];
    for (const page of allPages) {
      if (page.resetState) {
        page.resetState();
      }
    }
  };

  var loadFormListener = function () {
    const $content = $("#inventory-modal .modal-dynamic-content");
    $content.off("2N:FormInput", onFormInput);
    $content.on("2N:FormInput", onFormInput);
  };

  var onFormInput = function (e, value, path) {
    updateMonitoringScreeningStep();
    checkEnableSaveButton(value, path);
  };

  var updateMonitoringScreeningStep = function () {
    const data = getInventoryForRender();
    if (!("locationPhase" in data)) {
      return;
    }

    if (data.wqMonitoringLocation || data.dryWeatherMonitoringLocation) {
      InventoryModal.insertInventoryStep(
        getInventoryPages().length + 1,
        HardAssetInventoryMonitoringScreening,
      );
    } else {
      InventoryModal.removeInventoryStep(HardAssetInventoryMonitoringScreening);
    }
  };

  var checkEnableSaveButton = function (value, path) {
    const existingValue = getExistingData(path);
    if (existingValue !== undefined && value === existingValue) {
      Form.manuallyUnsetField(inventoryFormKey, path);
    }
    updateSaveButtonState();
  };

  var updateSaveButtonState = function () {
    InventoryModal.allowedSaveState(getMissingRequiredFieldWarning() === null);
    InventoryModal.readySaveState(!noUpdatesInForm());
  };

  var getMissingRequiredFieldWarning = function () {
    if (!getLatestData("name")) {
      return "Please enter an ID for this asset.";
    }
    if (!hasLocation()) {
      return "Please select a location for this asset on the map.";
    }
    if (isMissingRotation()) {
      return "Please enter a rotation for this asset.";
    }
    return null;
  };

  // Asset types without a rotation have no rotationAzimuth field at all
  var isMissingRotation = function () {
    const rotation = getLatestData("rotationAzimuth");
    return rotation === null || rotation === "";
  };

  var hasLocation = function () {
    return (
      isLineAsset() ||
      !!getLatestData("geom") ||
      (!!getLatestData("latitude") && !!getLatestData("longitude"))
    );
  };

  // Everything outside the hard blocks can be filled in later, so an incomplete asset is saved
  // once the user confirms
  var saveAndCloseModal = async function () {
    const missingFields = getMissingRequiredFields();
    if (missingFields.length > 0) {
      confirmSaveIncomplete(missingFields);
      return false;
    }

    return await saveInventory();
  };

  var confirmSaveIncomplete = function (missingFields) {
    highlightMissingRequiredFields();
    SaveIncompleteModal.showConfirmSaveIncompleteModal(
      getLatestData("name"),
      "inventory",
      missingFields.map((field) => field.label),
      saveInventory,
      InventoryModal.restoreAllButtons,
    );
  };

  var getMissingRequiredFields = function () {
    return HardAssetInventoryFields.getMissingRequiredFields(
      currentAssetType,
      getInventoryForRender(),
    );
  };

  var highlightMissingRequiredFields = function () {
    CommonModalFunctions.highlightFormFields(
      $("#inventory-modal .modal-dynamic-content"),
      getMissingRequiredFields().map((field) => field.name),
    );
  };

  var saveInventory = async function () {
    // Defaults the user accepted are not form edits, so they only reach the API from here
    const inventory = {
      ...appliedNewAssetDefaults,
      ...Form.getDataFromForm(inventoryFormKey, false),
    };
    appendDrawnDrainageGeometry(inventory);

    try {
      await ApiCalls.saveHardAssetInventory(currentAssetType, currentAssetId, inventory);
    } catch (e) {
      ApiError.assertIs(e);
      if (e.status !== 400) {
        throw e;
      }
      MessageModal.showSimpleWarningModal(e.xhrResponse.responseText);
      return false;
    }
    if (!isNewAsset()) {
      InspectableAssetCache.refreshAsset(currentAssetType, currentAssetId);
    }
    closeModal();
    // The data view swaps its layer by tab, and the legacy layer the To Do tab uses has no
    // invalidateLayerData. That layer is off the map on that tab anyway, so skipping the
    // refresh loses nothing here - the To Do list going stale after a save is separate.
    LayerFunctions.getCurrentLayerModule().invalidateLayerData?.();
    return false;
  };

  var downloadInventoryFile = function (filename, fileField) {
    return ApiCalls.downloadHardAssetInventoryFile(
      currentAssetType,
      currentAssetId,
      filename,
      fileField,
    );
  };

  var appendDrawnDrainageGeometry = function (inventory) {
    if (getLatestData("drawMethod") !== "drawing-tools" || !DrawController.getDrawnItems()) {
      return;
    }

    const polygons = DrawController.getDrawnDrainageGeom();
    if (polygons.length > 0) {
      inventory.drawnGeom = JSON.stringify(polygons);
    }
  };

  var closeModal = function () {
    Form.clearForm(inventoryFormKey);
    InventoryModal.hide();
  };

  var closeInventory = function () {
    Form.clearForm(inventoryFormKey);
  };

  var setModalTitle = function (data) {
    if (isNewAsset()) {
      InventoryModal.setModalTitle("Add New Asset");
    } else {
      InventoryModal.setModalTitle(`${data.assetDisplayType} ID: `, data.name);
    }
  };

  var noUpdatesInForm = function () {
    return Object.keys(Form.getDataFromForm(inventoryFormKey, false)).length === 0;
  };

  var getLatestData = function (path) {
    return InventoryModal.getLatestData(inventoryFormKey, Array.isArray(path) ? path : [path]);
  };

  var getExistingData = function (path) {
    const pathToCheck = Array.isArray(path) ? path : [path];
    return Tree.get(["asset", inventoryFormKey, ...pathToCheck]);
  };

  // The centralized BMPs of the catchment an asset was moved to replace the ones loaded with it,
  // which merging the data sources would combine index by index
  var getInventoryForRender = function () {
    const data = InventoryModal.getAllData(inventoryFormKey);

    if ("centralizedBmps" in data) {
      data.centralizedBmps = getLatestData("centralizedBmps");
    }

    return data;
  };

  var setFormDataField = function (path, value) {
    Form.manuallySetFormDataField(inventoryFormKey, Array.isArray(path) ? path : [path], value);
    updateSaveButtonState();
  };

  var renderPageContent = function (html) {
    return InventoryModal.setModalDynamicContent(html);
  };

  var rerenderPageSection = function ($page, sectionSelector, template, props) {
    const $oldSection = $page.find(sectionSelector);
    const focusedInputName = $oldSection.find(":focus").attr("name");
    const $section = $(nunjucks.render(template, props));

    $oldSection.replaceWith($section);
    Form.initializeAutoNumericFields($section);

    if (focusedInputName) {
      $section.find(`[name="${focusedInputName}"]`).trigger("focus");
    }
  };

  var getAssetType = function () {
    return currentAssetType;
  };

  var isLineAsset = function () {
    return getLatestData("isLineGeometry") === true;
  };

  var getInventoryFormKey = function () {
    return inventoryFormKey;
  };

  var onOpenInventoryClick = function () {
    const $el = $(this);
    invokeHardAssetInventoryModal($el.data("id"), $el.data("assetType"), $el.data("page"));
  };

  return {
    canEditInventory,
    invokeHardAssetInventoryModal,
    onOpenInventoryClick,
    getLatestData,
    getExistingData,
    getInventoryForRender,
    setFormDataField,
    renderPageContent,
    rerenderPageSection,
    getAssetType,
    getMissingRequiredFieldWarning,
    highlightMissingRequiredFields,
    downloadInventoryFile,
    isLineAsset,
    isNewAsset,
    getInventoryFormKey,
    saveAndCloseModal,
    updateSaveButtonState,
  };
};

module.exports = HardAssetInventoryModalController();

const ApiCalls = require("../apiCalls");
const ApiError = require("../errors/apiError");
const CommonModalFunctions = require("../modals/commonModalFunctions");
const DateTime = require("../dateTime");
const DrawController = require("../mapFunctions/drawController");
const Form = require("../general/form");
const HardAssetInventoryDrainageArea = require("./hardAsset/hardAssetInventoryDrainageArea");
const HardAssetInventoryConfig = require("./hardAsset/hardAssetInventoryConfig");
const HardAssetInventoryFields = require("./hardAsset/hardAssetInventoryFields");
const HardAssetInventoryGeneralInfo = require("./hardAsset/hardAssetInventoryGeneralInfo");
const HardAssetInventoryMonitoringScreening = require("./hardAsset/hardAssetInventoryMonitoringScreening");
const HardAssetInventorySetLocation = require("./hardAsset/hardAssetInventorySetLocation");
const InspectableAssetCache = require("../offline/inspectableAssetCache");
const InventoryModal = require("../general/inventoryModal");
const LayerFunctions = require("../layerFunctions");
const MessageModal = require("../modals/messageModal");
const SaveIncompleteModal = require("../modals/saveIncompleteModal");
const Tree = require("../tree");
const UserRoles = require("../login/userRoles");
